import {
  isPlatformBrowser,
  NgClass,
  NgOptimizedImage
} from '@angular/common';
import {
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  PLATFORM_ID,
  signal,
  ViewChild,
  WritableSignal
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators
} from '@angular/forms';
import { NgxTolgeeModule } from '@tolgee/ngx';

import type { Config } from 'browser-image-resizer';
import { accountCountries } from '../../../../../core/helpers/country.helper';
import { Gender } from '../../../../../core/profile/gender';
import { MessagingType } from '../../../../../core/profile/messaging-type';
import { emulateClick } from '../../core/a11y';
import { Error } from '../../core/error';
import { DrawerService } from '../../drawer/drawer.service';
import { NotificationService } from '../../notification/notification.service';
import { AutoCloseDirective } from '../../shared/directives/auto-close.directive';
import { HoverGradientDirective } from '../../shared/directives/hover-gradient.directive';
import { ProgressClickDirective } from '../../shared/directives/progress-click.directive';
import { blobToBase64 } from '../../shared/helper/file.helper';
import { ByPassPipe } from '../../shared/pipes/by-pass.pipe';
import { AuthService } from '../../shared/services/auth.service';
import { PostalCodeService } from '../../shared/services/postal-code.service';
import { TelInputComponent } from '../../shared/components/tel-input/tel-input.component';
import { FrenchMobileValidator } from '../../shared/validators/validators';
import { CenterService } from '../../shared/services/center.service';
import { ConfirmDeleteAccountComponent } from './confirm-delete-account/confirm-delete-account.component';

const config = {
  quality: 0.5,
  maxWidth: 320,
  maxHeight: 320,
};
type ProfileForm = {
  erpId: FormControl<number>;
  centerErpId: FormControl<number>;
  firstName: FormControl<string>;
  lastName: FormControl<string>;
  address: FormControl<string>;
  additionalAddress: FormControl<string>;
  zip: FormControl<string>;
  city: FormControl<string>;
  country: FormControl<string>;
  phone: FormControl<string>;
  gender: FormControl<Gender>;
  birthDay: FormControl<string>;
  birthMonth: FormControl<string>;
  birthYear: FormControl<string>;
  photoURL: FormControl<string>;
};

type CommunicationForm = {
  email: FormControl<boolean | null>;
  sms: FormControl<boolean | null>;
  courrier: FormControl<boolean | null>;
};

@Component({
  selector: 'app-profile',
  templateUrl: 'profile.component.html',
  imports: [
    TelInputComponent,
    AutoCloseDirective,
    ByPassPipe,
    HoverGradientDirective,
    NgxTolgeeModule,
    ProgressClickDirective,
    ReactiveFormsModule,
    NgOptimizedImage,
    NgClass
  ]
})
export class ProfileComponent {
  @ViewChild('avatarPicker') avatarPicker: ElementRef | undefined;
  authService = inject(AuthService);
  centerService = inject(CenterService);
  drawerService = inject(DrawerService);
  days = Array(31).fill(undefined).map((_, i) => (i + 1).toLocaleString(undefined, { minimumIntegerDigits: 2 }));
  months = Array(12).fill(undefined).map((_, i) => (i + 1).toLocaleString(undefined, { minimumIntegerDigits: 2 }));
  years = Array(new Date().getFullYear() - 1900 + 1).fill(undefined).map((_, i) => String(i + 1900));
  newProfileImage: WritableSignal<Blob | null> = signal(null);
  private postalCodes = inject(PostalCodeService);
  cities = signal<string[] | null>(null);
  form = computed(() => {
    const user = this.authService.user();
    const [ year, month, day ] = (user?.birthDate ?? '1970-01-01').split('-');
    const form = new FormGroup<ProfileForm>({
      erpId: new FormControl(user?.erpId ?? -1, { nonNullable: true, validators: [ Validators.required ] }),
      centerErpId: new FormControl(user?.centerErpId ?? this.centerService.center()?.erpId ?? -1,
        { nonNullable: true, validators: [ Validators.required, Validators.min(1) ] }),
      firstName: new FormControl(user?.firstName ?? '',
        { nonNullable: true, validators: [ Validators.required ] }),
      lastName: new FormControl(user?.lastName ?? '',
        { nonNullable: true, validators: [ Validators.required ] }),
      address: new FormControl(user?.address ?? '',
        { nonNullable: true, validators: [ Validators.required ] }),
      additionalAddress: new FormControl(user?.additionalAddress ?? '',
        { nonNullable: true }),
      zip: new FormControl(user?.zip ?? '', {
        nonNullable: true, validators: [ Validators.required, this.postalCodes.zipValidator ]
      }),
      city: new FormControl(user?.city ?? '',
        { nonNullable: true, validators: [ Validators.required ] }),
      country: new FormControl(user?.country ?? '',
        { nonNullable: true, validators: [ Validators.required ] }),
      phone: new FormControl(user?.phone ?? '', {
        nonNullable: true,
        validators: [ Validators.required, FrenchMobileValidator ]
      }),
      gender: new FormControl({ value: user?.gender ?? Gender.Male, disabled: true },
        { nonNullable: true, validators: [ Validators.required ] }),
      birthDay: new FormControl(day, { nonNullable: true, validators: [ Validators.required ] }),
      birthMonth: new FormControl(month, { nonNullable: true, validators: [ Validators.required ] }),
      birthYear: new FormControl(year, { nonNullable: true, validators: [ Validators.required, ] }),
      photoURL: new FormControl(user?.photoURL ?? '', { nonNullable: true }),
    });
    this.postalCodes.bind(form, this.cities);
    return form;
  });
  communicationForm = computed(() => {
    const user = this.authService.user();
    const notification = user?.notification ?? [];
    return new FormGroup<CommunicationForm>({
      email: new FormControl({
        value: notification.includes(MessagingType.Email), disabled: true
      },
      { nonNullable: true, validators: [ Validators.required ] }),
      sms: new FormControl({
        value: notification.includes(MessagingType.SMS), disabled: true
      },
      { nonNullable: true, validators: [ Validators.required ] }),
      courrier: new FormControl({
        value: notification.includes(MessagingType.Courrier), disabled: true
      },
      { nonNullable: true, validators: [ Validators.required ] }),
    })
  });
  edit = signal(false);
  editCommunication = signal(false);
  progress = signal(false);
  displayName = computed(() => {
    const user = this.authService.user();
    if (user?.firstName || user?.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }
    return user?.email ?? '--';
  });
  activeCenters = computed(() => {
    return this.centerService.centers().filter(c => c.enabled && c.erpId > 0)
  });
  displayCities = computed(() => {
    const cities = this.cities();
    const current = this.form().get('city')?.value;
    return cities?.every(c => c !== current) ?? false;
  });
  protected readonly ConfirmDeleteAccountComponent = ConfirmDeleteAccountComponent;
  protected readonly emulateClick = emulateClick;
  protected readonly Gender = Gender;
  protected readonly sortedCountries = (): ReturnType<typeof accountCountries> => accountCountries(this.authService.user()?.country);
  protected readonly MessagingType = MessagingType;
  private notificationService = inject(NotificationService);

  constructor() {
    if (isPlatformBrowser(inject(PLATFORM_ID))) {
      import('browser-image-resizer').then(m => this.readAndCompressImage = m.default.readAndCompressImage)
    }

    effect(() => {
      const edit = this.edit();
      if (edit) {
        this.form().enable();
        this.communicationForm().disable();
      } else {
        this.form().disable();
      }
    });
    effect(() => {
      const editCommunication = this.editCommunication();
      if (editCommunication) {
        this.communicationForm().enable();
        this.form().disable();
      } else {
        this.communicationForm().disable();
      }
    });
    effect(() => {
      const progress = this.progress();
      if (progress) {
        this.form().get('phone')?.disable()
      } else {
        this.form().get('phone')?.enable()
      }
    });
  }

  /**
   * Call update profile API with updated user data
   */
  updateProfile = async (): Promise<void> => {
    const form = this.form();
    const communicationForm = this.communicationForm();
    const shouldUpdateConsent = !communicationForm.valid;
    form.disable();
    communicationForm.disable();
    const profileData = form.getRawValue();
    const communicationData = communicationForm.getRawValue();
    const user = this.authService.user();
    if (user) {
      this.progress.set(true);
      const file = this.newProfileImage();
      if (file) {
        profileData.photoURL = await this.authService.updateAvatar(file);
        this.newProfileImage.set(null);
      }
      await this.authService.updateProfile({
        erpId: profileData.erpId,
        centerErpId: profileData.centerErpId,
        firstName: profileData.firstName,
        lastName: profileData.lastName,
        photoURL: profileData.photoURL,
        phone: profileData.phone,
        address: profileData.address,
        additionalAddress: profileData.additionalAddress,
        zip: profileData.zip,
        city: profileData.city,
        country: profileData.country,
        birthDate: `${profileData.birthYear}-${profileData.birthMonth}-${profileData.birthDay}`,
        gender: profileData.gender,
        notification: Object.entries(communicationData)
          .filter(entry => entry[1])
          .map(entry => entry[0]) as MessagingType[],
        shouldUpdateCenter: user.shouldUpdateCenter,
        shouldUpdateConsent: shouldUpdateConsent
      });
      await this.authService.getIdToken(true);
      this.progress.set(false);
    }
    this.edit.set(false);
  }

  /**
   * Set view in edit profile state
   */
  enableEditProfile(): void {
    this.edit.set(true);
  }

  /**
   * Set view in edit communication state
   */
  enableEditCommunication(): void {
    this.editCommunication.set(true);
  }

  /**
   * Update communication preferences
   */
  saveCommunicationPreference = async (): Promise<void> => {
    await this.updateProfile();
    this.editCommunication.set(false);
  }

  /**
   * Email the user to reset the password
   * @returns The promise
   */
  resetPassword = async (): Promise<void> => {
    const email = this.authService.user()?.email ?? '';
    if (!email) {
      return;
    }
    try {
      await this.authService.resetPassword(email);
      this.notificationService.open({ type: 'message', title: 'auth.resetPassword', message: 'checkInbox' });
      this.drawerService.close();
    } catch (err) {
      const error = err as Error;
      this.notificationService.open({ type: 'error', title: error.details.code, message: error.message });
    }
  }

  /**
   * Check file weight and upload file into Firebase Storage
   * @param event Select File event
   */
  async updatePhotoUrlForm(event: Event): Promise<void> {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) {
      return;
    }
    const resizedImage = await this.readAndCompressImage(file, config);
    if (resizedImage.size > 1024 * 1024) {
      this.notificationService.open({ type: 'warning', title: 'tooBig.title', message: 'tooBig.message' });
      return;
    }
    const base64 = await blobToBase64(resizedImage);
    if (base64) {
      this.form().get('photoURL')?.setValue(base64);
      this.form().updateValueAndValidity();
      if (this.avatarPicker) {
        this.avatarPicker.nativeElement.value = '';
      }
      this.newProfileImage.set(resizedImage);
    }
  }

  isValidDate(year: string, month: string, day: string): boolean {
    const date = new Date(Date.UTC(Number(year), Number(month) - 1, Number(day)));
    return date.getTime() <= Date.now();
  }

  hasCityList(): boolean {
    return this.postalCodes.hasCityList(this.form().get('country')?.value ?? '');
  }

  setCity(city: string): void {
    this.cities.set(null);
    this.form().get('city')?.setValue(city);
    this.form().updateValueAndValidity();
  }

  private readAndCompressImage: (file: File,
                                 userConfig?: Config
  ) => Promise<Blob> = () => Promise.reject("Not initialized");
}
